import { useState } from "react";
import { motion } from "framer-motion";
import { 
  ThumbsUp, 
  MessageSquare, 
  Repeat2, 
  Share2, 
  ExternalLink, 
  CheckCircle2, 
  Globe2,
  Sparkles,
  Bookmark
} from "lucide-react";
import { SITE } from "../../constants/site";

// Authentic LinkedIn SVG Icon
export function LinkedInIcon({ className = "w-4 h-4", fill = "currentColor" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.64a1.59 1.59 0 0 0-1.6 1.59 1.6 1.6 0 0 0 1.6 1.6 1.6 1.6 0 0 0 1.6-1.6 1.59 1.59 0 0 0-1.6-1.59Z" />
    </svg>
  );
}

// Format relative date helper
function getRelativeTime(dateStr) {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 30) {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    if (diffDays > 0) return `${diffDays}d • `;
    if (diffHours > 0) return `${diffHours}h • `;
    if (diffMins > 0) return `${diffMins}m • `;
    return "Just now • ";
  } catch {
    return "Recent • ";
  }
}

export default function LinkedInPostCard({ post, index = 0, onTagClick }) {
  const [expanded, setExpanded] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes_count || 45);
  const [copied, setCopied] = useState(false);

  const linkedinCompanyUrl = SITE.linkedin || "https://www.linkedin.com/company/crescet-edustreams-consulting/posts";
  const postUrl = post.post_url || linkedinCompanyUrl;

  const toggleLike = () => {
    if (hasLiked) {
      setHasLiked(false);
      setLikeCount((c) => Math.max(0, c - 1));
    } else {
      setHasLiked(true);
      setLikeCount((c) => c + 1);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Crescet Edustreams LinkedIn Post",
          text: post.content.slice(0, 100),
          url: postUrl,
        });
        return;
      } catch {
        // Fallback to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.open(postUrl, "_blank", "noopener,noreferrer");
    }
  };

  const charThreshold = 180;
  const isLong = post.content && post.content.length > charThreshold;
  const displayContent = expanded || !isLong 
    ? post.content 
    : post.content.slice(0, charThreshold) + "...";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, duration: 0.4 }}
      className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200/90 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#0077B5]/40 hover:shadow-xl"
      data-testid={`linkedin-post-${post.id}`}
    >
      {/* Top Banner Accent */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#0077B5] via-[#0B5D4B] to-[#C89B3C]" />

      <div className="p-5 sm:p-6">
        {/* Post Author / Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <a
              href={linkedinCompanyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative shrink-0 transition-transform duration-200 hover:scale-105"
            >
              <div className="h-12 w-12 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                <img
                  src={post.author_avatar || "/crescet-favicon.png"}
                  alt={post.author_name || "Crescet Edustreams"}
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#0077B5] text-white ring-2 ring-white">
                <LinkedInIcon className="h-2.5 w-2.5" fill="#ffffff" />
              </span>
            </a>

            <div>
              <div className="flex items-center gap-1.5">
                <a
                  href={linkedinCompanyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold text-slate-900 transition-colors hover:text-[#0077B5] sm:text-base"
                >
                  {post.author_name || "Crescet Edustreams Consulting"}
                </a>
                <CheckCircle2 size={15} className="text-[#0077B5] shrink-0" fill="#0077B5" stroke="#ffffff" />
              </div>
              <p className="line-clamp-1 text-xs text-slate-500 font-medium">
                {post.author_tagline || "DPIIT-Recognized EdTech Ecosystem"}
              </p>
              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                <span>{getRelativeTime(post.published_at)}</span>
                <Globe2 size={11} className="text-slate-400" />
                <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                  LinkedIn
                </span>
              </div>
            </div>
          </div>

          {/* Quick Direct Link Button */}
          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open on LinkedIn"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-all hover:bg-[#0077B5]/10 hover:text-[#0077B5]"
          >
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Post Text Content */}
        <div className="mt-4 text-xs sm:text-sm leading-relaxed text-slate-700">
          <p className="whitespace-pre-line font-normal">
            {displayContent}
          </p>

          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1 font-semibold text-[#0077B5] hover:underline focus:outline-none"
            >
              {expanded ? "Show less" : "...see more"}
            </button>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {post.tags.map((tag, idx) => (
                <button
                  key={idx}
                  onClick={() => onTagClick && onTagClick(tag)}
                  className="rounded-lg bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-[#0077B5] transition-colors hover:bg-sky-100"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Media Preview / Embed */}
        {post.embed_url ? (
          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            <iframe
              src={post.embed_url}
              height="380"
              width="100%"
              frameBorder="0"
              allowFullScreen=""
              title="LinkedIn Embedded Post"
              className="w-full"
            />
          </div>
        ) : post.media_url ? (
          <div className="mt-4 relative aspect-[16/9] w-full overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
            <img
              src={post.media_url}
              alt="LinkedIn post visual"
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute bottom-3 right-3 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur flex items-center gap-1.5">
              <LinkedInIcon className="h-3 w-3" fill="#ffffff" />
              <span>Official Post</span>
            </div>
          </div>
        ) : null}

        {/* Engagement Stats Row */}
        <div className="mt-4 flex items-center justify-between border-b border-slate-100 pb-3 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <div className="flex -space-x-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0077B5] text-white ring-2 ring-white">
                <ThumbsUp size={10} fill="#ffffff" />
              </span>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#C89B3C] text-white ring-2 ring-white">
                <Sparkles size={10} fill="#ffffff" />
              </span>
            </div>
            <span className="font-semibold text-slate-700">{likeCount}</span>
          </div>

          <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium">
            <span>{post.comments_count || 8} comments</span>
            <span>&middot;</span>
            <span>{post.shares_count || 5} reposts</span>
          </div>
        </div>

        {/* Action Buttons Bar */}
        <div className="mt-2.5 flex items-center justify-between pt-1">
          <button
            onClick={toggleLike}
            className={`flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              hasLiked
                ? "bg-[#0077B5]/10 text-[#0077B5]"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <ThumbsUp size={14} className={hasLiked ? "fill-[#0077B5]" : ""} />
            <span>Like</span>
          </button>

          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <MessageSquare size={14} />
            <span>Comment</span>
          </a>

          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <Repeat2 size={15} />
            <span>Repost</span>
          </a>

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <Share2 size={14} />
            <span>{copied ? "Copied!" : "Share"}</span>
          </button>
        </div>
      </div>

      {/* Bottom LinkedIn Direct CTA */}
      <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-3 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <LinkedInIcon className="h-3.5 w-3.5 text-[#0077B5]" fill="#0077B5" />
          <span>Follow on LinkedIn for real-time alerts</span>
        </div>
        <a
          href={postUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-bold text-[#0077B5] hover:text-[#005582] transition-colors"
        >
          View Post
          <ExternalLink size={12} />
        </a>
      </div>
    </motion.div>
  );
}
